/* biome-ignore-all lint/suspicious/noArrayIndexKey: Structured blocks are controlled values without local component state; position is their document identity. */
import { platforms } from "@ondit/publishing";
import {
	Button,
	ChipSelect,
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
	Input,
	Label,
	NativeSelect,
	Textarea,
} from "@ondit/ui";
import { ChevronDown } from "@ondit/ui/icons";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { useAccess } from "~/core/access";
import { ApiError } from "~/core/api";
import { pick, useI18n, useT } from "~/core/i18n";
import presets from "../../../../packages/content/media-presets.json";
import {
	type ContentDocument,
	type ContentItem,
	type ContentKind,
	emptyDocument,
	fileUrl,
	listContent,
	readContent,
	type StudioFile,
	studioCommand,
	studioKey,
	studioOptions,
} from "./api";
import { issueLabel } from "./issues";
import { VariantEditor } from "./variant";
import "./studio.css";

const names: Record<ContentKind, [string, string]> = {
	text: ["Short text", "短文本"],
	article: ["Article", "文章"],
	gallery: ["Image post", "图文"],
	video: ["Video", "视频"],
};
export function StudioList({ brandId }: { brandId: string }) {
	const { locale } = useI18n();
	const tr = (en: string, zh: string) => pick(locale, [en, zh]);
	const { editor } = useAccess();
	const navigate = useNavigate();
	const query = useQuery({ queryKey: studioKey(brandId), queryFn: () => listContent(brandId) });
	const [title, setTitle] = useState("");
	const [kind, setKind] = useState<ContentKind>("article");
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState("");
	async function create() {
		setBusy(true);
		setError("");
		try {
			const item = await studioCommand<{ id: string }>(brandId, "studio.save", {
				title,
				kind,
				locale,
				document: emptyDocument(),
			});
			await query.refetch();
			navigate(`/w/${brandId}/agent/home?studio=1&content=${item.id}`);
		} catch (e) {
			setError(e instanceof ApiError ? e.localized(locale) : String(e));
		} finally {
			setBusy(false);
		}
	}
	return (
		<section className="studio-workspace">
			<header className="record-heading">
				<h1>{tr("Content studio", "素材制作")}</h1>
				<Link to={`/w/${brandId}/agent/files`}>{tr("Brand files", "品牌文件")}</Link>
			</header>
			<form
				onSubmit={(e) => {
					e.preventDefault();
					void create();
				}}
			>
				<fieldset disabled={!editor || busy} className="studio-toolbar bare-fieldset">
					<Label htmlFor="studio-title">{tr("Topic", "主题")}</Label>
					<Input id="studio-title" value={title} onChange={(e) => setTitle(e.target.value)} required maxLength={200} />
					<NativeSelect
						aria-label={tr("Format", "内容形式")}
						value={kind}
						onChange={(e) => setKind(e.target.value as ContentKind)}
					>
						{Object.entries(names).map(([key, value]) => (
							<option key={key} value={key}>
								{pick(locale, value)}
							</option>
						))}
					</NativeSelect>
					<Button type="submit" variant="primary" disabled={!title.trim()}>
						{tr("Create with agent", "与智能体一起制作")}
					</Button>
				</fieldset>
			</form>
			{error && <p role="alert">{error}</p>}
			{query.error && <p role="alert">{query.error.message}</p>}
			{query.isPending ? (
				<p>{tr("Loading…", "加载中…")}</p>
			) : query.data?.items.length === 0 ? (
				<p className="hint">
					{tr(
						"Create a work, then reuse its text and media across platforms.",
						"创建作品，将文字与素材复用于不同平台。",
					)}
				</p>
			) : (
				<div className="studio-list">
					{query.data?.items.map((item) => (
						<Link key={item.id} to={`/w/${brandId}/studio/${item.id}`}>
							<strong>{item.title}</strong>
							<span>
								{pick(locale, names[item.kind])} · {tr("Revision", "修订")} {item.revision}
							</span>
						</Link>
					))}
				</div>
			)}
		</section>
	);
}
export function StudioEditor({
	brandId,
	itemId,
	embedded = false,
}: {
	brandId: string;
	itemId: string;
	embedded?: boolean;
}) {
	const t = useT();
	const { locale } = useI18n();
	const tr = (en: string, zh: string) => pick(locale, [en, zh]);
	const { editor } = useAccess();
	const cache = useQueryClient();
	const query = useQuery({
		queryKey: [...studioKey(brandId), itemId],
		queryFn: () => readContent(brandId, itemId),
		refetchInterval: (query) =>
			query.state.data?.renders?.some((render) => ["queued", "running"].includes(render.status)) ? 4000 : false,
	});
	const options = useQuery({ queryKey: [...studioKey(brandId), "options"], queryFn: () => studioOptions(brandId) });
	const [model, setModel] = useState("");
	const [prompt, setPrompt] = useState("");
	const library = useQuery({ queryKey: studioKey(brandId), queryFn: () => listContent(brandId) });
	const [draft, setDraft] = useState<ContentItem | null>(null);
	const [dirty, setDirty] = useState(false);
	const [variantDirty, setVariantDirty] = useState(false);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState("");
	const [platform, setPlatform] = useState("");
	const [view, setView] = useState("edit");
	const [device, setDevice] = useState("mobile");
	const [preset, setPreset] = useState("portrait");
	useEffect(() => {
		if (query.data && !dirty && !variantDirty) setDraft(query.data);
	}, [query.data, dirty, variantDirty]);

	const item = draft;
	const changed = !!item && !!query.data && item.revision !== query.data.revision;
	function update(document: ContentDocument) {
		setDraft((d) => (d ? { ...d, document } : d));
		setDirty(true);
	}
	async function action(capability: string, input: unknown) {
		setBusy(true);
		setError("");
		try {
			await studioCommand(brandId, capability, input);
			setDirty(false);
			await cache.invalidateQueries({ queryKey: studioKey(brandId) });
		} catch (e) {
			setError(e instanceof ApiError ? e.localized(locale) : String(e));
		} finally {
			setBusy(false);
		}
	}
	if (!item)
		return (
			<div className="studio-workspace">
				{query.error ? <p role="alert">{query.error.message}</p> : tr("Loading…", "加载中…")}
			</div>
		);
	const variant = item.variants?.find((v) => v.platform === platform);
	const contract = platforms.find((p) => p.id === platform);
	const files = new Map([...(library.data?.files ?? []), ...(item.files ?? [])].map((f) => [f.id, f]));
	const doc = item.document;
	function attach(id: string) {
		const f = files.get(id);
		if (!f) return;
		const role = f.content_type.startsWith("video/")
			? "video"
			: f.content_type.startsWith("audio/")
				? "audio"
				: f.content_type.startsWith("image/")
					? "image"
					: "subtitle";
		update({ ...doc, media: [...doc.media, { file_id: id, role, alt: f.name }] });
	}
	return (
		<section className="studio-workspace" data-embedded={embedded}>
			<header className="record-heading">
				<div>
					<h2>{item.title}</h2>
					<span className="hint">
						{pick(locale, names[item.kind])} · {tr("Revision", "修订")} {item.revision}
					</span>
				</div>
				<Link to={`/w/${brandId}/generate/studio`}>{tr("All works", "全部作品")}</Link>
				{!embedded && (
					<Link to={`/w/${brandId}/agent/home?studio=1&content=${item.id}`}>
						{tr("Continue with agent", "与智能体继续制作")}
					</Link>
				)}
			</header>
			<div className="studio-toolbar">
				<Button variant={view === "edit" ? "active" : "ghost"} onClick={() => setView("edit")}>
					{tr("Edit", "编辑")}
				</Button>
				<Button variant={view === "preview" ? "active" : "ghost"} onClick={() => setView("preview")}>
					{tr("Preview", "预览")}
				</Button>
				<ChipSelect
					aria-label={tr("Platform", "平台")}
					placeholder={tr("Standard master", "标准母版")}
					value={platform}
					onChange={(value) => {
						setVariantDirty(false);
						setPlatform(value);
					}}
					options={[
						{ value: "", label: tr("Standard master", "标准母版") },
						...platforms.map((p) => ({ value: p.id, label: p.name })),
					]}
				/>
			</div>
			{error && <p role="alert">{error}</p>}
			{changed && (
				<p role="alert">
					{tr(
						"This work changed elsewhere. Reload before saving your edits.",
						"作品已在其他位置更新，请重新载入后再修改。",
					)}
					<Button
						onClick={() => {
							setDirty(false);
							setVariantDirty(false);
							setDraft(query.data ?? null);
						}}
					>
						{tr("Reload", "重新载入")}
					</Button>
				</p>
			)}
			{platform && dirty ? (
				<p role="status">
					{tr("Save the master changes before editing a platform version.", "请先保存母版修改，再编辑平台版本。")}
				</p>
			) : platform && variant && contract ? (
				<VariantEditor
					onDirty={setVariantDirty}
					key={`${variant.id}-${variant.revision}`}
					brandId={brandId}
					item={item}
					variant={variant}
					platform={contract}
					files={new Map(item.files.map((f) => [f.id, f]))}
				/>
			) : view === "edit" ? (
				<fieldset disabled={!editor || busy} className="studio-form bare-fieldset">
					<Label htmlFor={`title-${itemId}`}>{tr("Title", "标题")}</Label>
					<Input
						id={`title-${itemId}`}
						value={item.title}
						onChange={(e) => {
							setDraft({ ...item, title: e.target.value });
							setDirty(true);
						}}
					/>
					<Label htmlFor={`body-${itemId}`}>{tr("Body / script", "正文 / 脚本")}</Label>
					<Textarea
						id={`body-${itemId}`}
						value={doc.body}
						rows={10}
						onChange={(e) => update({ ...doc, body: e.target.value })}
					/>
					{doc.blocks.map((block, i) => (
						<div className="studio-part" key={`${i}-${block.type}`}>
							<Label>
								{block.type === "scene"
									? tr("Scene", "分镜")
									: block.type === "image"
										? tr("Inline image", "正文插图")
										: tr("Paragraph", "段落")}{" "}
								{i + 1}
							</Label>
							{block.type === "image" ? (
								<Media file={files.get(block.file_id)} alt={block.alt} />
							) : (
								<Textarea
									value={block.text}
									onChange={(e) =>
										update({
											...doc,
											blocks: doc.blocks.map((b, n) => (n === i ? { ...block, text: e.target.value } : b)),
										})
									}
								/>
							)}
							{block.type === "scene" && (
								<div className="studio-toolbar">
									<Input
										type="number"
										min={1}
										max={120}
										aria-label={tr("Seconds", "秒数")}
										value={block.seconds}
										onChange={(e) =>
											update({
												...doc,
												blocks: doc.blocks.map((b, n) => (n === i ? { ...block, seconds: Number(e.target.value) } : b)),
											})
										}
									/>
									<NativeSelect
										aria-label={tr("Scene image", "分镜图片")}
										value={block.file_id ?? ""}
										onChange={(e) =>
											update({
												...doc,
												blocks: doc.blocks.map((b, n) => (n === i ? { ...block, file_id: e.target.value || null } : b)),
											})
										}
									>
										<option value="">{tr("Text card", "文字卡片")}</option>
										{[...files.values()]
											.filter((f) => f.content_type.startsWith("image/"))
											.map((f) => (
												<option key={f.id} value={f.id}>
													{f.name}
												</option>
											))}
									</NativeSelect>
								</div>
							)}
							<Button variant="ghost" onClick={() => update({ ...doc, blocks: doc.blocks.filter((_, n) => n !== i) })}>
								{tr("Remove", "移除")}
							</Button>
						</div>
					))}
					<div className="studio-toolbar">
						<Button
							onClick={() =>
								update({
									...doc,
									blocks: [
										...doc.blocks,
										item.kind === "video"
											? { type: "scene", text: "", seconds: 5, file_id: null }
											: { type: "text", text: "" },
									],
								})
							}
						>
							{tr("Add section", "添加段落 / 分镜")}
						</Button>
						<Link to={`/w/${brandId}/agent/files`}>{tr("Upload in files", "在文件中上传")}</Link>
					</div>
					<Label htmlFor={`assets-${itemId}`}>{tr("Use a brand file", "使用品牌文件")}</Label>
					<NativeSelect id={`assets-${itemId}`} value="" onChange={(e) => attach(e.target.value)}>
						<option value="">{tr("Select a file…", "选择文件…")}</option>
						{(library.data?.files ?? [])
							.filter((f) => /^(image|video|audio)\//.test(f.content_type) || /\.(srt|vtt)$/.test(f.name))
							.map((f) => (
								<option key={f.id} value={f.id}>
									{f.name}
								</option>
							))}
					</NativeSelect>
					{item.kind === "article" && (
						<NativeSelect
							aria-label={tr("Insert inline image", "插入正文图片")}
							value=""
							onChange={(e) =>
								update({
									...doc,
									blocks: [
										...doc.blocks,
										{ type: "image", file_id: e.target.value, alt: files.get(e.target.value)?.name ?? "" },
									],
								})
							}
						>
							<option value="">{tr("Insert inline image…", "插入正文图片…")}</option>
							{[...files.values()]
								.filter((f) => f.content_type.startsWith("image/"))
								.map((f) => (
									<option key={f.id} value={f.id}>
										{f.name}
									</option>
								))}
						</NativeSelect>
					)}
					<div className="studio-assets">
						{doc.media.map((asset, i) => (
							<figure key={`${asset.file_id}-${i}`}>
								<Media
									file={files.get(asset.file_id)}
									alt={asset.alt}
									caption={doc.media.find((a) => a.role === "subtitle")?.file_id}
								/>
								<Input
									aria-label={tr("Description", "素材说明")}
									value={asset.alt}
									onChange={(e) =>
										update({ ...doc, media: doc.media.map((a, n) => (n === i ? { ...a, alt: e.target.value } : a)) })
									}
								/>
								<div className="studio-toolbar">
									<Button
										disabled={i === 0}
										onClick={() => {
											const media = [...doc.media];
											[media[i - 1], media[i]] = [media[i], media[i - 1]];
											update({ ...doc, media });
										}}
									>
										{tr("Move earlier", "前移")}
									</Button>
									<Button
										variant="ghost"
										onClick={() => update({ ...doc, media: doc.media.filter((_, n) => n !== i) })}
									>
										{tr("Remove", "移除")}
									</Button>
								</div>
							</figure>
						))}
					</div>
					<Label htmlFor={`model-${itemId}`}>{tr("Image / narration model", "图片 / 配音模型")}</Label>
					<NativeSelect id={`model-${itemId}`} value={model} onChange={(e) => setModel(e.target.value)}>
						<option value="">{tr("Choose a model…", "选择模型…")}</option>
						{options.data?.models.map((m) => (
							<option key={m.id} value={m.id}>
								{m.name || m.id} · {m.modality} · {m.credits_per_1000_input.toFixed(2)} /{" "}
								{m.credits_per_1000_output.toFixed(2)}{" "}
								{tr("credits per 1k input/output units", "积分 / 千输入、输出单位")}
							</option>
						))}
					</NativeSelect>
					<Textarea
						aria-label={tr("Image brief / narration text", "图片描述 / 配音文本")}
						value={prompt}
						onChange={(e) => setPrompt(e.target.value)}
						maxLength={4000}
						placeholder={tr("Describe the image, or enter the exact narration.", "描述需要的图片，或填写配音原文。")}
					/>
					<div className="studio-toolbar">
						<Button
							disabled={!model || !prompt.trim() || dirty}
							onClick={() =>
								void action("studio.generate", {
									id: item.id,
									expected_revision: item.revision,
									modality: options.data?.models.find((m) => m.id === model)?.modality,
									model,
									prompt,
									preset,
								})
							}
						>
							{tr("Generate media", "生成素材")}
						</Button>
						<span className="hint">
							{tr(
								"Uses credits at the selected model's rate. Save changes before generating.",
								"按所选模型实际用量扣除积分；生成前请先保存修改。",
							)}
						</span>
					</div>
					{options.error && <p role="alert">{options.error.message}</p>}
					<div className="studio-toolbar">
						<NativeSelect
							aria-label={tr("Output format", "输出规格")}
							value={preset}
							onChange={(e) => setPreset(e.target.value)}
						>
							{presets.presets.map((p) => (
								<option key={p.id} value={p.id}>
									{p.label[locale === "zh" ? 1 : 0]} · {p.width} × {p.height}
								</option>
							))}
						</NativeSelect>
						{item.kind === "video" ? (
							<DropdownMenu>
								<DropdownMenuTrigger asChild>
									<Button disabled={dirty || busy}>
										{tr("Render", "制作")}
										<ChevronDown />
									</Button>
								</DropdownMenuTrigger>
								<DropdownMenuContent align="start">
									<DropdownMenuItem
										onSelect={() =>
											void action("studio.render", {
												id: item.id,
												expected_revision: item.revision,
												format: "cards",
												preset,
											})
										}
									>
										{tr("Image cards", "图文卡片")}
									</DropdownMenuItem>
									<DropdownMenuItem
										onSelect={() =>
											void action("studio.render", {
												id: item.id,
												expected_revision: item.revision,
												format: "video",
												preset,
											})
										}
									>
										{tr("Video and captions", "视频与字幕")}
									</DropdownMenuItem>
								</DropdownMenuContent>
							</DropdownMenu>
						) : (
							<Button
								disabled={dirty || busy}
								onClick={() =>
									void action("studio.render", {
										id: item.id,
										expected_revision: item.revision,
										format: "cards",
										preset,
									})
								}
							>
								{tr("Render image cards", "制作图文卡片")}
							</Button>
						)}
					</div>
					{item.renders?.map((render) => (
						<div key={render.id} role="status">
							{tr("Production", "制作任务")} · {t(`tasks.${render.status}`)}
							{render.output?.attached === false && (
								<span>
									{tr(" — Files saved; the work changed during production.", " — 文件已保存，制作期间作品已更新。")}
								</span>
							)}
							{render.error && <p role="alert">{render.error}</p>}
						</div>
					))}
					<Label htmlFor={`sources-${itemId}`}>
						{tr("Sources (one HTTPS URL per line)", "来源（每行一个 HTTPS 链接）")}
					</Label>
					<Textarea
						id={`sources-${itemId}`}
						rows={2}
						value={doc.sources.join("\n")}
						onChange={(e) => update({ ...doc, sources: e.target.value.split("\n").filter(Boolean) })}
					/>
					<div className="studio-toolbar">
						<Button
							variant="primary"
							disabled={!dirty || changed}
							onClick={() =>
								void action("studio.save", {
									id: item.id,
									title: item.title,
									kind: item.kind,
									locale: item.locale,
									expected_revision: item.revision,
									document: doc,
								})
							}
						>
							{tr("Save revision", "保存修订")}
						</Button>
						<Button
							disabled={dirty || !platform}
							onClick={() =>
								void action("studio.adapt", { id: item.id, expected_revision: item.revision, platforms: [platform] })
							}
						>
							{tr("Prepare platform version", "准备平台版本")}
						</Button>
					</div>
				</fieldset>
			) : (
				<>
					<div className="studio-toolbar">
						<NativeSelect aria-label={tr("Device", "设备")} value={device} onChange={(e) => setDevice(e.target.value)}>
							<option value="mobile">{tr("Mobile", "移动端")}</option>
							<option value="desktop">{tr("Desktop", "桌面端")}</option>
						</NativeSelect>
						<span className="hint">
							{tr("Layout simulation; platforms may reprocess media.", "版式模拟；平台可能再次处理素材。")}
						</span>
					</div>
					{platform && !variant ? (
						<p>{tr("Save and prepare this platform version first.", "请先保存并准备此平台版本。")}</p>
					) : (
						<article
							className="studio-preview"
							data-device={device}
							data-layout={contract?.preview.layout ?? item.kind}
						>
							<header>
								<span className="hint">{contract?.name ?? tr("Master", "母版")}</span>
								<h3>{variant?.document.fields.title ?? item.title}</h3>
							</header>
							<div className="studio-preview-body">{variant?.document.fields.body ?? doc.body}</div>
							<div className="studio-preview-media">
								{(variant?.document.media ?? doc.media).map((a, i) => (
									<figure key={`${a.file_id}-${i}`}>
										<Media
											file={files.get(a.file_id)}
											alt={a.alt}
											caption={doc.media.find((a) => a.role === "subtitle")?.file_id}
										/>
										{a.alt && <figcaption>{a.alt}</figcaption>}
									</figure>
								))}
							</div>
							{!variant &&
								doc.blocks.map((b, i) =>
									b.type === "image" ? (
										<figure key={i}>
											<Media file={files.get(b.file_id)} alt={b.alt} />
										</figure>
									) : (
										<p className="studio-preview-body" key={i}>
											{b.text}
										</p>
									),
								)}
						</article>
					)}
					{variant && variant.issues.length > 0 && (
						<div role="status">
							<strong>{tr("Needs attention before delivery", "交付前需要处理")}</strong>
							<ul>
								{variant.issues.map((issue) => (
									<li key={issue}>{issueLabel(issue, locale)}</li>
								))}
							</ul>
						</div>
					)}
				</>
			)}
		</section>
	);
}
function Media({ file, alt, caption }: { file: StudioFile | undefined; alt: string; caption?: string }) {
	if (!file) return <span>{alt}</span>;
	const src = fileUrl(file.id);
	return file.content_type.startsWith("video/") ? (
		// biome-ignore lint/a11y/useMediaCaption: Caption tracks are rendered when the work has an actual subtitle asset.
		<video src={src} controls preload="metadata" aria-label={alt}>
			{caption && <track kind="captions" src={fileUrl(caption)} default />}
		</video>
	) : file.content_type.startsWith("audio/") ? (
		// biome-ignore lint/a11y/useMediaCaption: Uploaded audio may have no subtitle asset; never invent a transcript.
		<audio src={src} controls aria-label={alt}>
			{caption && <track kind="captions" src={fileUrl(caption)} default />}
		</audio>
	) : file.content_type.startsWith("image/") ? (
		<img src={src} alt={alt} />
	) : (
		<a href={src} download>
			{file.name}
		</a>
	);
}

export function StudioPage({ brandId }: { brandId: string }) {
	const { itemId } = useParams();
	return <StudioEditor key={itemId} brandId={brandId} itemId={itemId!} />;
}
